import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { eliminarOSuspender } from '@/lib/destruccion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { CircleHelp, CopyPlus, Plus, RefreshCw, Printer, Send, Mail, ShieldCheck, Camera, MoreVertical, Pencil, Power, Trash2, Upload, Loader2, X, ImagePlus, FileDown } from 'lucide-react';
import PhoneInput from '@/components/PhoneInput';
import ImportCarDealerModal from '@/components/ImportCarDealerModal';
import ImportUsedVehiclesModal from '@/components/ImportUsedVehiclesModal';
import CopyableId from '@/components/CopyableId';

const MEXICO_BRANDS = ['Nissan', 'Chevrolet', 'Volkswagen', 'Toyota', 'Kia', 'Mazda', 'Hyundai', 'Renault', 'Honda', 'Ford', 'Jeep', 'Chirey', 'MG', 'BYD', 'Otra'];
const BODY_TYPES = ['SUV', 'Sedán', 'Hatchback', 'Pickup', 'Coupé', 'Van', 'Minivan', 'Crossover'];
const CATEGORIES = ['Nuevo', 'Seminuevo'];

const EQUIPMENT_ITEMS = [
  'Pantalla táctil', 'Cámara 360°', 'Apple CarPlay', 'Android Auto', 'Asientos de piel', 'Techo panorámico',
  'Sensor de reversa', 'Sensor frontal', 'Control crucero', 'Control crucero adaptativo', 'Frenado autónomo',
  'Asistente de carril', 'Monitoreo de punto ciego', 'Faros LED', 'Encendido remoto', 'Apertura sin llave',
  'Climatizador automático', 'Asientos calefactables', 'Asientos ventilados', 'Volante calefactable',
  'Airbags frontales', 'Airbags laterales', 'Airbags de cortina', 'ABS', 'EBD', 'Control de estabilidad',
  'Tracción inteligente', 'Modos de manejo', 'Cargador inalámbrico', 'Puerto USB-C', 'Sonido premium',
  'Subwoofer', 'Navegación integrada', 'Head-Up Display', 'Cámara de reversa', 'Sistema de parking autónomo',
  'Rines de aleación', 'Luces ambientales', 'Portón eléctrico', 'Asientos eléctricos', 'Memoria de asiento',
  'Alarma', 'Inmovilizador', 'GPS antirobo', 'Dashcam', 'ISOFIX', 'Sensor de lluvia', 'Sensor de luz',
  'Espejos eléctricos', 'Espejos abatibles', 'Quemacocos', 'Start/Stop', 'Auto Hold', 'Freno de estacionamiento eléctrico'
];

const Hint = ({ text, testid }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="inline-flex text-zinc-500 hover:text-zinc-200" data-testid={testid}><CircleHelp className="w-3.5 h-3.5" /></button>
    </TooltipTrigger>
    <TooltipContent className="max-w-[250px] text-xs">{text}</TooltipContent>
  </Tooltip>
);

const emptyVehicle = {
  general: { brand: '', brand_other: '', model: '', trim: '', trims: [], year: new Date().getFullYear(), body_type: 'SUV', category: 'Nuevo' },
  technical: {
    engine_displacement: '', engine_type: '', cylinders: '', power_hp: '', power_kw: '', torque_nm: '',
    transmission_type: '', gears: '', traction: '', fuel_type: '',
    eff_city: '', eff_highway: '', eff_combined: '', tank_capacity: '',
    length: '', width: '', height: '', wheelbase: '', ground_clearance: '', trunk_capacity: '', seats: '',
  },
  pricing_inventory: {
    list_price: 0, sale_price: 0, max_discount: 0, dealer_special_price: 0, stock: 0,
    vin: '', mileage: '', condition: '', location: '',
  },
  media: { gallery: [], video_url: '', tech_sheet_pdf_url: '' },
  equipment: { checklist: [], included_accessories: [], optional_accessories: [] },
  is_active: true,
};

const parseLines = (value) => String(value || '').split('\n').map(v => v.trim()).filter(Boolean);
const formatLines = (arr) => (arr || []).join('\n');

const emptyUsedVehicle = {
  marca: '', modelo: '', año: new Date().getFullYear(),
  carroceria: 'SUV', transmision: 'Automática',
  color_exterior: '', color_interior: '',
  combustible: 'Gasolina', puertas: 4,
  kilometraje: 0, precio: 0,
  estatus: 'Disponible',
  vin: '', location: '', video_url: '',
  gallery: [],
  is_active: true,
};

export default function CarDealerPage() {
  const [tab, setTab] = useState('modelos');
  const [models, setModels] = useState([]);
  const [used, setUsed] = useState([]);
  const usedVehicles = used;
  const setUsedVehicles = setUsed;
  const [appointments, setAppointments] = useState([]);
  const [promoImages, setPromoImages] = useState([]);
  const [promoUploading, setPromoUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vehicleStep, setVehicleStep] = useState('general');
  const [editingVehicleId, setEditingVehicleId] = useState('');
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(0);
  const mediaFileInputRef = useRef(null);

  const [usedForm, setUsedForm] = useState(emptyUsedVehicle);
  const [usedModalOpen, setUsedModalOpen] = useState(false);
  const [usedImportModalOpen, setUsedImportModalOpen] = useState(false);
  const [editingUsed, setEditingUsed] = useState(null);
  const [usedTab, setUsedTab] = useState('datos');
  const [uploadingUsedPhoto, setUploadingUsedPhoto] = useState(false);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [financialQuote, setFinancialQuote] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    vehicle_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    lead_id: '',
    opportunity_id: '',
    vehicle_price: 0,
    down_payment_pct: 20,
    down_payment_amount: 0,
    annual_interest_rate: 19.9,
    iva_pct: 16,
    opening_commission_pct: 2.5,
    term_months: 60,
    driver_age: 32,
    coverage_type: 'amplia',
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, u, a, q, site] = await Promise.all([
        api.get('/car-dealer/vehicles'),
        api.get('/car-dealer/used'),
        api.get('/car-dealer/appointments'),
        api.get('/car-dealer/financial-quotes').catch(() => ({ data: [] })),
        api.get('/car-builder/site').catch(() => ({ data: {} })),
      ]);
      setModels(m.data || []);
      setUsed(u.data || []);
      setAppointments(a.data || []);
      setQuoteHistory(q.data || []);
      setPromoImages(site.data?.hero_images || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando DMS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);
  const activeModels = useMemo(() => models.filter(m => m.is_active), [models]);
  const selectedQuoteVehicle = useMemo(() => models.find(m => m.id === quoteForm.vehicle_id), [models, quoteForm.vehicle_id]);
  const money = (value) => Number(value || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

  const openNewVehicle = () => {
    setEditingVehicleId('');
    setVehicleForm(emptyVehicle);
    setVehicleStep('general');
    setVehicleOpen(true);
  };

  // Exportar inventario de modelos a CSV con IDs, marca, modelo, año, gama,
  // precio, stock y estado activo. Solo se invoca desde el header del tab Modelos.
  const exportModelsCSV = () => {
    const escape = (val) => {
      const s = val === null || val === undefined ? '' : String(val);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['ID', 'Marca', 'Modelo', 'Año', 'Gama', 'Precio', 'Stock', 'Activo'];
    const rows = (models || []).map(m => [
      m.id || '',
      m.general?.brand || m.brand || '',
      m.general?.model || m.name || '',
      m.general?.year || m.year || '',
      m.general?.trim || '',
      m.pricing_inventory?.sale_price ?? m.pricing_inventory?.list_price ?? '',
      m.pricing_inventory?.stock ?? '',
      m.is_active ? 'Sí' : 'No',
    ]);
    // BOM UTF-8 para que Excel reconozca los acentos al abrir el CSV.
    const csv = '\ufeff' + [headers, ...rows]
      .map(r => r.map(escape).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventario_modelos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportUsedCSV = () => {
    const headers = [
      'id','marca','modelo','año','carroceria','transmision',
      'color_exterior','color_interior','combustible','puertas',
      'kilometraje','precio','estatus','vin','location','video_url'
    ];
    const rows = usedVehicles.map(v => headers.map(h => {
      const val = v[h] ?? '';
      const str = String(val).replace(/"/g, '""');
      return str.includes(',') || str.includes('\n') || str.includes('"')
        ? `"${str}"` : str;
    }).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'seminuevos.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEditVehicle = (vehicle) => {
    setEditingVehicleId(vehicle.id);
    setVehicleForm({
      ...emptyVehicle,
      ...vehicle,
      general: { ...emptyVehicle.general, ...(vehicle.general || {}), trim: (vehicle.general || {}).trim || '', trims: (vehicle.general || {}).trims || [] },
      technical: { ...emptyVehicle.technical, ...(vehicle.technical || {}) },
      pricing_inventory: { ...emptyVehicle.pricing_inventory, ...(vehicle.pricing_inventory || {}) },
      media: { ...emptyVehicle.media, ...(vehicle.media || {}) },
      equipment: { ...emptyVehicle.equipment, ...(vehicle.equipment || {}) },
    });
    setVehicleStep('general');
    setVehicleOpen(true);
  };

  const saveVehicle = async (andCreateAnotherTrim = false) => {
    try {
      const payload = {
        ...vehicleForm,
        general: {
          ...vehicleForm.general,
          year: Number(vehicleForm.general?.year || 0),
          trims: Array.from(new Set([...(vehicleForm.general?.trims || []), vehicleForm.general?.trim].filter(Boolean))),
        },
        pricing_inventory: {
          ...vehicleForm.pricing_inventory,
          list_price: Number(vehicleForm.pricing_inventory?.list_price || 0),
          sale_price: Number(vehicleForm.pricing_inventory?.sale_price || 0),
          max_discount: Number(vehicleForm.pricing_inventory?.max_discount || 0),
          dealer_special_price: Number(vehicleForm.pricing_inventory?.dealer_special_price || 0),
          stock: Number(vehicleForm.pricing_inventory?.stock || 0),
        },
      };

      if (editingVehicleId) await api.put(`/car-dealer/vehicles/${editingVehicleId}`, payload);
      else await api.post('/car-dealer/vehicles', payload);

      toast.success(editingVehicleId ? 'Modelo actualizado' : 'Modelo guardado');
      await loadAll();

      if (andCreateAnotherTrim) {
        setEditingVehicleId('');
        setVehicleForm(prev => ({
          ...prev,
          general: { ...prev.general, trim: '', category: 'Nuevo' },
          pricing_inventory: { ...prev.pricing_inventory, vin: '', mileage: '', condition: '', stock: 0 },
        }));
        setVehicleStep('general');
        setVehicleOpen(true);
      } else {
        setVehicleOpen(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el modelo');
    }
  };

  const duplicateVehicle = async (vehicleId) => {
    try {
      await api.post(`/car-dealer/vehicles/${vehicleId}/duplicate`, { is_active: false });
      toast.success('Modelo duplicado. Edita y ajusta la nueva versión.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo duplicar el modelo');
    }
  };

  const openCreateUsed = () => {
    setEditingUsed(null);
    setUsedForm(emptyUsedVehicle);
    setUsedTab('datos');
    setUsedModalOpen(true);
  };

  const openEditUsed = (vehicle) => {
    setEditingUsed(vehicle);
    setUsedForm({ ...emptyUsedVehicle, ...vehicle });
    setUsedTab('datos');
    setUsedModalOpen(true);
  };

  const handleUsedPhotoUpload = async (files) => {
    setUploadingUsedPhoto(true);
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setUsedForm(prev => ({
          ...prev,
          gallery: [...prev.gallery, { url: res.data.url, is_360: false, label: '' }]
        }));
      } catch (e) {
        console.error('Error subiendo foto:', e);
        toast.error(e.response?.data?.detail || 'Error subiendo foto');
      }
    }
    setUploadingUsedPhoto(false);
  };

  const removeUsedPhoto = (idx) => {
    setUsedForm(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== idx)
    }));
  };

  const toggleUsed360 = (idx) => {
    setUsedForm(prev => ({
      ...prev,
      gallery: prev.gallery.map((img, i) =>
        i === idx ? { ...img, is_360: !img.is_360 } : img
      )
    }));
  };

  const saveUsedVehicle = async () => {
    try {
      if (editingUsed) {
        const res = await api.put(`/car-dealer/used/${editingUsed.id}`, usedForm);
        setUsedVehicles(prev => prev.map(v => v.id === editingUsed.id ? res.data : v));
      } else {
        const res = await api.post('/car-dealer/used', usedForm);
        setUsedVehicles(prev => [res.data, ...prev]);
      }
      setUsedModalOpen(false);
      toast.success('Seminuevo guardado');
    } catch (e) {
      console.error('Error guardando seminuevo:', e);
      toast.error(e.response?.data?.detail || 'No se pudo guardar seminuevo');
    }
  };

  const deleteUsedVehicle = async (id) => {
    if (!window.confirm('¿Eliminar este seminuevo?')) return;
    try {
      await api.delete(`/car-dealer/used/${id}`);
      setUsedVehicles(prev => prev.filter(v => v.id !== id));
      toast.success('Seminuevo eliminado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const savePromoImages = async (images) => {
    await api.put('/car-builder/site', { hero_images: images });
  };

  const handlePromoUpload = async (file) => {
    if (!file) return;
    setPromoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData);
      const url = res.data?.url;
      if (!url) throw new Error('No se recibió URL');
      const newImages = [...promoImages, url];
      setPromoImages(newImages);
      await savePromoImages(newImages);
      toast.success('Imagen agregada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setPromoUploading(false);
    }
  };

  const handlePromoDelete = async (url) => {
    const newImages = promoImages.filter(u => u !== url);
    setPromoImages(newImages);
    try {
      await savePromoImages(newImages);
      toast.success('Imagen eliminada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const generateFinancialQuote = async () => {
    if (!quoteForm.vehicle_id) {
      toast.error('Selecciona un vehículo');
      return;
    }
    if (!quoteForm.customer_name || !quoteForm.customer_phone || !quoteForm.customer_email) {
      toast.error('Nombre, teléfono y email son obligatorios');
      return;
    }
    setQuoteLoading(true);
    try {
      const payload = {
        ...quoteForm,
        vehicle_price: Number(quoteForm.vehicle_price || selectedQuoteVehicle?.pricing_inventory?.sale_price || 0),
        insurance: {},
      };
      const response = await api.post('/car-dealer/financial-quotes', payload);
      setFinancialQuote(response.data);
      setQuoteHistory((prev) => [response.data, ...prev.filter(item => item.id !== response.data.id)]);
      toast.success('Cotización financiera creada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar cotización financiera');
    } finally {
      setQuoteLoading(false);
    }
  };

  const sendQuote = async (channel) => {
    if (!financialQuote?.id) {
      toast.error('Primero genera una cotización');
      return;
    }
    try {
      const payload = {
        send_whatsapp: channel === 'whatsapp',
        send_email: channel === 'email',
        phone: quoteForm.customer_phone,
        email: quoteForm.customer_email,
      };
      await api.post(`/car-dealer/financial-quotes/${financialQuote.id}/send`, payload);
      toast.success(channel === 'whatsapp' ? 'Cotización enviada por WhatsApp' : 'Cotización enviada por Email');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar la cotización');
    }
  };

  const printQuote = () => {
    if (!financialQuote) {
      toast.error('No hay cotización para imprimir');
      return;
    }
    const image = financialQuote?.vehicle_snapshot?.image || '';
    const popup = window.open('', '_blank', 'width=980,height=760');
    if (!popup) return;
    popup.document.write(`
      <html><head><title>Cotización ${financialQuote.id}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
        .header { display: flex; justify-content: space-between; gap: 16px; align-items: center; }
        .car-image { width: 280px; height: 170px; object-fit: cover; border-radius: 10px; border: 1px solid #ddd; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 18px; }
        .card { border: 1px solid #ddd; border-radius: 10px; padding: 10px; }
        table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 12px; }
        th, td { border: 1px solid #eee; padding: 6px; text-align: right; }
        th:first-child, td:first-child { text-align: left; }
      </style></head>
      <body>
        <div class="header">
          <div>
            <h2>Cotización Financiera Automotriz</h2>
            <p><strong>Cliente:</strong> ${financialQuote.customer?.name || '-'}</p>
            <p><strong>Vehículo:</strong> ${financialQuote.vehicle_snapshot?.brand || ''} ${financialQuote.vehicle_snapshot?.model || ''} ${financialQuote.vehicle_snapshot?.year || ''}</p>
          </div>
          ${image ? `<img src="${image}" class="car-image"/>` : ''}
        </div>
        <div class="grid">
          <div class="card"><strong>Mensualidad</strong><br/>${money(financialQuote.breakdown?.monthly_payment)}</div>
          <div class="card"><strong>Total a pagar</strong><br/>${money(financialQuote.breakdown?.total_paid)}</div>
          <div class="card"><strong>CAT estimado</strong><br/>${Number(financialQuote.breakdown?.cat_estimated_pct || 0).toFixed(2)}%</div>
        </div>
        <table>
          <thead><tr><th>Mes</th><th>Pago</th><th>Interés</th><th>Capital</th><th>Saldo</th></tr></thead>
          <tbody>
            ${(financialQuote.amortization || []).slice(0, 12).map(row => `<tr><td>${row.month}</td><td>${money(row.payment)}</td><td>${money(row.interest)}</td><td>${money(row.principal)}</td><td>${money(row.balance)}</td></tr>`).join('')}
          </tbody>
        </table>
      </body></html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="car-dealer-page">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-zinc-950 to-zinc-900 p-5">
          <h1 className="text-3xl font-black" data-testid="car-dealer-title">Car-Dealer DMS</h1>
          <p className="text-sm text-zinc-400 mt-1" data-testid="car-dealer-subtitle">Sistema profesional de inventario, gamas, refacciones, servicios y postventa.</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="car-dealer-tabs">
          <TabsList className="grid grid-cols-6">
            <TabsTrigger value="modelos" data-testid="car-tab-modelos">Modelos</TabsTrigger>
            <TabsTrigger value="seminuevos" data-testid="car-tab-seminuevos">Seminuevos</TabsTrigger>
            <TabsTrigger value="promociones" data-testid="car-tab-promociones">Promociones</TabsTrigger>
            <TabsTrigger value="citas" data-testid="car-tab-citas">Citas</TabsTrigger>
            <TabsTrigger value="cotizador" data-testid="car-tab-cotizador">Cotizador</TabsTrigger>
          </TabsList>

          <TabsContent value="cotizador" className="space-y-4" data-testid="car-financial-quote-tab">
            <Card className="border-cyan-500/20 bg-cyan-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" data-testid="car-quote-form-title"><ShieldCheck className="w-4 h-4 text-cyan-300" />Cotizador Financiero</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid md:grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-zinc-400">Vehículo</label>
                    <Select value={quoteForm.vehicle_id || ''} onValueChange={(value) => {
                      const model = models.find(m => m.id === value);
                      setQuoteForm(prev => ({
                        ...prev,
                        vehicle_id: value,
                        vehicle_price: Number(model?.pricing_inventory?.sale_price || model?.pricing_inventory?.list_price || 0),
                      }));
                    }}>
                      <SelectTrigger data-testid="car-quote-vehicle-select"><SelectValue placeholder="Selecciona modelo" /></SelectTrigger>
                      <SelectContent>{models.map(item => <SelectItem key={item.id} value={item.id}>{item.general?.brand} {item.general?.model} {item.general?.trim || ''}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input value={quoteForm.customer_name} onChange={e => setQuoteForm(prev => ({ ...prev, customer_name: e.target.value }))} placeholder="Nombre cliente" data-testid="car-quote-customer-name" />
                  <PhoneInput data-testid="car-quote-customer-phone" value={quoteForm.customer_phone} onChange={v => setQuoteForm(prev => ({ ...prev, customer_phone: v }))} />
                  <Input value={quoteForm.customer_email} onChange={e => setQuoteForm(prev => ({ ...prev, customer_email: e.target.value }))} placeholder="Email cliente" data-testid="car-quote-customer-email" />
                  <Input value={quoteForm.lead_id} onChange={e => setQuoteForm(prev => ({ ...prev, lead_id: e.target.value }))} placeholder="Lead ID (opcional)" data-testid="car-quote-lead-id" />
                  <Input value={quoteForm.opportunity_id} onChange={e => setQuoteForm(prev => ({ ...prev, opportunity_id: e.target.value }))} placeholder="Opportunity ID (opcional)" data-testid="car-quote-opportunity-id" />
                </div>

                {selectedQuoteVehicle?.media?.gallery?.[0]?.url && (
                  <div className="rounded-xl overflow-hidden border border-white/10 max-w-sm" data-testid="car-quote-vehicle-image-wrap">
                    <img src={selectedQuoteVehicle.media.gallery[0].url} alt="Vehículo" className="w-full h-44 object-cover" data-testid="car-quote-vehicle-image" />
                  </div>
                )}

                <div className="grid md:grid-cols-4 gap-2">
                  <Input type="number" value={quoteForm.vehicle_price} onChange={e => setQuoteForm(prev => ({ ...prev, vehicle_price: Number(e.target.value || 0) }))} placeholder="Precio vehículo" data-testid="car-quote-vehicle-price" />
                  <Input type="number" value={quoteForm.down_payment_pct} onChange={e => setQuoteForm(prev => ({ ...prev, down_payment_pct: Number(e.target.value || 0) }))} placeholder="Enganche %" data-testid="car-quote-down-payment-pct" />
                  <Input type="number" value={quoteForm.down_payment_amount} onChange={e => setQuoteForm(prev => ({ ...prev, down_payment_amount: Number(e.target.value || 0) }))} placeholder="Enganche monto" data-testid="car-quote-down-payment-amount" />
                  <Input type="number" value={quoteForm.term_months} onChange={e => setQuoteForm(prev => ({ ...prev, term_months: Number(e.target.value || 0) }))} placeholder="Plazo meses" data-testid="car-quote-term-months" />
                  <Input type="number" value={quoteForm.annual_interest_rate} onChange={e => setQuoteForm(prev => ({ ...prev, annual_interest_rate: Number(e.target.value || 0) }))} placeholder="Tasa anual %" data-testid="car-quote-rate" />
                  <Input type="number" value={quoteForm.iva_pct} onChange={e => setQuoteForm(prev => ({ ...prev, iva_pct: Number(e.target.value || 0) }))} placeholder="IVA %" data-testid="car-quote-iva" />
                  <Input type="number" value={quoteForm.opening_commission_pct} onChange={e => setQuoteForm(prev => ({ ...prev, opening_commission_pct: Number(e.target.value || 0) }))} placeholder="Comisión apertura %" data-testid="car-quote-opening-fee" />
                  <Input type="number" value={quoteForm.driver_age} onChange={e => setQuoteForm(prev => ({ ...prev, driver_age: Number(e.target.value || 0) }))} placeholder="Edad conductor" data-testid="car-quote-driver-age" />
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button onClick={generateFinancialQuote} disabled={quoteLoading} data-testid="car-quote-generate-button">Generar cotización financiera</Button>
                </div>
              </CardContent>
            </Card>

            {financialQuote && (
              <Card data-testid="car-financial-quote-preview-card">
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-semibold" data-testid="car-financial-quote-id">Cotización #{financialQuote.id}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={printQuote} data-testid="car-quote-print-button"><Printer className="w-3.5 h-3.5 mr-1" />Imprimir</Button>
                      <Button variant="outline" onClick={() => sendQuote('whatsapp')} data-testid="car-quote-send-whatsapp-button"><Send className="w-3.5 h-3.5 mr-1" />WA</Button>
                      <Button variant="outline" onClick={() => sendQuote('email')} data-testid="car-quote-send-email-button"><Mail className="w-3.5 h-3.5 mr-1" />Email</Button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-2 text-sm">
                    <div className="rounded-lg border border-white/10 p-2" data-testid="car-financial-monthly-payment"><p className="text-zinc-400 text-xs">Mensualidad</p><p className="font-semibold">{money(financialQuote.breakdown?.monthly_payment)}</p></div>
                    <div className="rounded-lg border border-white/10 p-2" data-testid="car-financial-total-paid"><p className="text-zinc-400 text-xs">Total a pagar</p><p className="font-semibold">{money(financialQuote.breakdown?.total_paid)}</p></div>
                    <div className="rounded-lg border border-white/10 p-2" data-testid="car-financial-total-interest"><p className="text-zinc-400 text-xs">Intereses</p><p className="font-semibold">{money(financialQuote.breakdown?.total_interest)}</p></div>
                    <div className="rounded-lg border border-white/10 p-2" data-testid="car-financial-cat"><p className="text-zinc-400 text-xs">CAT estimado</p><p className="font-semibold">{Number(financialQuote.breakdown?.cat_estimated_pct || 0).toFixed(2)}%</p></div>
                  </div>
                  <div className="overflow-x-auto" data-testid="car-financial-amortization-table-wrap">
                    <table className="w-full text-xs" data-testid="car-financial-amortization-table">
                      <thead>
                        <tr className="border-b border-white/10 text-zinc-400"><th className="text-left py-1">Mes</th><th className="text-right py-1">Pago</th><th className="text-right py-1">Interés</th><th className="text-right py-1">Capital</th><th className="text-right py-1">Saldo</th></tr>
                      </thead>
                      <tbody>
                        {(financialQuote.amortization || []).slice(0, 12).map((row) => (
                          <tr key={row.month} className="border-b border-white/5" data-testid={`car-financial-row-${row.month}`}><td className="py-1">{row.month}</td><td className="text-right">{money(row.payment)}</td><td className="text-right">{money(row.interest)}</td><td className="text-right">{money(row.principal)}</td><td className="text-right">{money(row.balance)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle data-testid="car-financial-history-title">Historial de cotizaciones ({quoteHistory.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2" data-testid="car-financial-history-list">
                {quoteHistory.map((item) => (
                  <button key={item.id} type="button" className="w-full text-left rounded-lg border border-white/10 p-3 hover:border-cyan-500/30 transition-colors" onClick={() => { setFinancialQuote(item); setQuoteForm(prev => ({ ...prev, customer_name: item.customer?.name || prev.customer_name, customer_phone: item.customer?.phone || prev.customer_phone, customer_email: item.customer?.email || prev.customer_email })); }} data-testid={`car-financial-history-item-${item.id}`}>
                    <p className="text-sm font-semibold">{item.vehicle_snapshot?.brand} {item.vehicle_snapshot?.model} · {item.customer?.name}</p>
                    <p className="text-xs text-zinc-400">{money(item.breakdown?.monthly_payment)} mensual · {new Date(item.created_at).toLocaleDateString('es-MX')}</p>
                  </button>
                ))}
                {!quoteHistory.length && <p className="text-sm text-zinc-400" data-testid="car-financial-history-empty">Sin cotizaciones aún.</p>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="modelos" className="space-y-4">
            <div className="flex items-center justify-between" data-testid="car-models-actions-row">
              <p className="text-sm text-zinc-400">Modelos activos publicados en Car Builder: <span className="text-white font-semibold" data-testid="car-active-models-count">{activeModels.length}</span></p>
              <div className="flex items-center gap-2">
                {models.length > 0 && (
                  <Button onClick={exportModelsCSV} variant="outline" data-testid="car-export-csv-btn"><FileDown className="w-4 h-4 mr-1" /> Exportar CSV</Button>
                )}
                <Button onClick={() => setImportModalOpen(true)} variant="outline" data-testid="car-open-import-modal"><Upload className="w-4 h-4 mr-1" /> Importar Excel</Button>
                <Button onClick={openNewVehicle} data-testid="car-open-new-model-modal"><Plus className="w-4 h-4 mr-1" /> Nuevo / Editar Modelo</Button>
              </div>
            </div>

            <Card>
              <CardHeader><CardTitle data-testid="car-models-title">Inventario de modelos y gamas</CardTitle></CardHeader>
              <CardContent className="p-0" data-testid="car-models-list">
                {!models.length ? (
                  <p className="text-sm text-zinc-400 p-4" data-testid="car-model-empty">Sin modelos todavía.</p>
                ) : (
                  <div className="relative max-h-[70vh] overflow-x-auto overflow-y-auto">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead className="sticky top-0 z-20 bg-[#1F2833] text-zinc-300 uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">ID</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Marca / Modelo</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Trim / Gama</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Carrocería</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Año</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Motor / HP</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Transmisión</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap text-center">Plazas</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap text-right">Precio lista</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap text-right">Precio financiado</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Estado</th>
                          <th className="sticky right-0 z-30 bg-[#1F2833] px-3 py-2 font-semibold whitespace-nowrap text-center shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.4)]">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {models.map((m) => {
                          const trim = m.general?.trim || '-';
                          const listPrice = Number(m.pricing_inventory?.list_price || 0);
                          const finPrice = Number(m.pricing_inventory?.dealer_special_price || 0);
                          const hp = m.technical?.power_hp ? `${m.technical.power_hp} HP` : '';
                          const engineDisp = m.technical?.engine_displacement || '';
                          const motorLabel = [engineDisp, hp].filter(Boolean).join(' • ') || '-';
                          return (
                            <tr key={m.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`car-model-row-${m.id}`}>
                              <td className="px-3 py-2 whitespace-nowrap"><CopyableId id={m.id} /></td>
                              <td className="px-3 py-2 whitespace-nowrap font-medium text-white">{m.general?.brand || m.brand || '-'} {m.general?.model || m.name || ''}</td>
                              <td className="px-3 py-2 max-w-[180px] truncate text-zinc-300" title={trim}>{trim}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{m.general?.body_type || m.general?.category || m.vehicle_type || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{m.general?.year || m.year || '-'}</td>
                              <td className="px-3 py-2 max-w-[160px] truncate text-zinc-300" title={motorLabel}>{motorLabel}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{m.technical?.transmission_type || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-center text-zinc-300">{m.technical?.seats || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-zinc-100">{listPrice ? `$${listPrice.toLocaleString('es-MX')}` : '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-zinc-100">{finPrice ? `$${finPrice.toLocaleString('es-MX')}` : '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded border ${m.is_active ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-zinc-500/40 text-zinc-400 bg-zinc-500/10'}`} data-testid={`car-model-status-${m.id}`}>{m.is_active ? 'Activo' : 'Inactivo'}</span>
                              </td>
                              <td className="sticky right-0 z-10 bg-[#0F1419] group-hover:bg-[#161b22] px-3 py-2 whitespace-nowrap shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.4)]">
                                <div className="flex items-center justify-end gap-1">
                                  {(() => {
                                    const photoCount = (m.media?.gallery || []).length;
                                    const hasPhotos = photoCount > 0;
                                    return (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className={`h-7 w-7 relative ${hasPhotos ? 'text-amber-300 hover:text-amber-200' : ''}`}
                                        onClick={() => { openEditVehicle(m); setVehicleStep('media'); }}
                                        data-testid={`car-model-photos-${m.id}`}
                                        title={hasPhotos ? `${photoCount} foto${photoCount === 1 ? '' : 's'} cargada${photoCount === 1 ? '' : 's'}` : 'Sin fotos — clic para subir'}
                                      >
                                        <Camera className="w-3.5 h-3.5" />
                                        {hasPhotos && (
                                          <span
                                            className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-amber-400 text-black text-[9px] font-bold flex items-center justify-center px-0.5 border border-[#0F1419]"
                                            data-testid={`car-model-photos-badge-${m.id}`}
                                          >
                                            {photoCount > 9 ? '9+' : photoCount}
                                          </span>
                                        )}
                                      </Button>
                                    );
                                  })()}
                                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openEditVehicle(m)} data-testid={`car-model-edit-${m.id}`}>
                                    <Pencil className="w-3 h-3 mr-1" />Editar
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`car-model-menu-${m.id}`} title="Más acciones">
                                        <MoreVertical className="w-3.5 h-3.5" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-40">
                                      <DropdownMenuItem onClick={() => duplicateVehicle(m.id)} data-testid={`car-model-duplicate-${m.id}`}>
                                        <CopyPlus className="w-3.5 h-3.5 mr-2" /> Duplicar
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={async () => { await api.put(`/car-dealer/vehicles/${m.id}`, { is_active: !m.is_active }); loadAll(); }} data-testid={`car-model-toggle-${m.id}`}>
                                        <Power className="w-3.5 h-3.5 mr-2" /> {m.is_active ? 'Desactivar' : 'Activar'}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem className="text-red-400 focus:text-red-400" onClick={() => eliminarOSuspender(`/car-dealer/vehicles/${m.id}`, { etiqueta: 'Vehículo', confirmar: '¿Retirar este vehículo de la operación?', alTerminar: loadAll })} data-testid={`car-model-delete-${m.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Eliminar
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            <ImportCarDealerModal
              open={importModalOpen}
              onClose={() => setImportModalOpen(false)}
              onSuccess={loadAll}
            />
          </TabsContent>

          <TabsContent value="seminuevos" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                Seminuevos activos:{' '}
                <span className="text-white font-semibold">
                  {usedVehicles.filter(v => v.is_active).length}
                </span>
              </p>
              <div className="flex items-center gap-2">
                {usedVehicles.length > 0 && (
                  <Button onClick={exportUsedCSV} variant="outline">
                    <FileDown className="w-4 h-4 mr-1" /> Exportar CSV
                  </Button>
                )}
                <Button
                  onClick={() => setUsedImportModalOpen(true)}
                  variant="outline"
                  data-testid="car-used-import-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Importar Excel
                </Button>
                <Button onClick={openCreateUsed} data-testid="car-used-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Agregar Seminuevo
                </Button>
              </div>
            </div>

            <Card>
              <CardHeader><CardTitle>Inventario de Seminuevos</CardTitle></CardHeader>
              <CardContent className="p-0" data-testid="car-used-list">
                {!usedVehicles.length ? (
                  <p className="text-sm text-zinc-400 p-4" data-testid="car-used-empty">
                    Sin seminuevos todavía.
                  </p>
                ) : (
                  <div className="relative max-h-[70vh] overflow-x-auto overflow-y-auto">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead className="sticky top-0 z-20 bg-[#1F2833] text-zinc-300 uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">ID</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Marca / Modelo</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Año</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Carrocería</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Transmisión</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap text-right">Kilometraje</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap text-right">Precio</th>
                          <th className="px-3 py-2 font-semibold whitespace-nowrap">Estatus</th>
                          <th className="sticky right-0 z-30 bg-[#1F2833] px-3 py-2 font-semibold whitespace-nowrap text-center shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.4)]">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usedVehicles.map((v) => {
                          const precio = Number(v.precio || 0);
                          const km = Number(v.kilometraje || 0);
                          const photoCount = (v.gallery || []).length;
                          return (
                            <tr key={v.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`car-used-item-${v.id}`}>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <CopyableId id={v.id} />
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap font-medium text-white">
                                {v.marca} {v.modelo}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{v.año || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{v.carroceria || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{v.transmision || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-zinc-300">
                                {km ? `${km.toLocaleString('es-MX')} km` : '-'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-zinc-100">
                                {precio ? `$${precio.toLocaleString('es-MX')}` : '-'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded border ${
                                  v.estatus === 'Disponible' ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' :
                                  v.estatus === 'Apartado'   ? 'border-yellow-500/40 text-yellow-300 bg-yellow-500/10' :
                                                               'border-red-500/40 text-red-300 bg-red-500/10'
                                }`}>
                                  {v.estatus || 'Disponible'}
                                </span>
                              </td>
                              <td className="sticky right-0 z-10 bg-[#0F1419] px-3 py-2 whitespace-nowrap shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.4)]">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className={`h-7 w-7 relative ${photoCount > 0 ? 'text-amber-300 hover:text-amber-200' : ''}`}
                                    onClick={() => { openEditUsed(v); setUsedTab('galeria'); }}
                                    title={photoCount > 0 ? `${photoCount} foto${photoCount === 1 ? '' : 's'}` : 'Sin fotos — clic para subir'}
                                  >
                                    <Camera className="w-3.5 h-3.5" />
                                    {photoCount > 0 && (
                                      <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-amber-400 text-black text-[9px] font-bold flex items-center justify-center px-0.5 border border-[#0F1419]">
                                        {photoCount > 9 ? '9+' : photoCount}
                                      </span>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[11px]"
                                    onClick={() => openEditUsed(v)}
                                    data-testid={`car-used-edit-${v.id}`}
                                  >
                                    <Pencil className="w-3 h-3 mr-1" />Editar
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Más acciones">
                                        <MoreVertical className="w-3.5 h-3.5" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-40">
                                      <DropdownMenuItem
                                        onClick={async () => {
                                          await api.put(`/car-dealer/used/${v.id}`, { ...v, is_active: !v.is_active });
                                          loadAll();
                                        }}
                                      >
                                        <Power className="w-3.5 h-3.5 mr-2" />{v.is_active ? 'Desactivar' : 'Activar'}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-red-400 focus:text-red-400"
                                        onClick={() => deleteUsedVehicle(v.id)}
                                        data-testid={`car-used-delete-${v.id}`}
                                      >
                                        <Trash2 className="w-3.5 h-3.5 mr-2" />Eliminar
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            <ImportUsedVehiclesModal
              open={usedImportModalOpen}
              onClose={() => setUsedImportModalOpen(false)}
              onSuccess={loadAll}
            />
          </TabsContent>

          <TabsContent value="promociones" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle data-testid="car-promo-title">Imágenes del Hero</CardTitle>
                <p className="text-xs text-zinc-400 mt-1">
                  Estas imágenes se muestran en el carrusel principal de la landing.
                  Si hay más de una se rotan automáticamente.
                </p>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="car-promo-content">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-zinc-400">
                    Imágenes:{' '}
                    <span className="text-white font-semibold">
                      {promoImages.length}
                    </span>
                  </p>
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handlePromoUpload(f);
                        e.target.value = '';
                      }}
                      disabled={promoUploading}
                      data-testid="car-promo-upload-input"
                    />
                    <Button
                      type="button"
                      disabled={promoUploading}
                      onClick={(e) => e.currentTarget.previousSibling?.click()}
                      data-testid="car-promo-upload-btn"
                    >
                      {promoUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" /> Subir imagen
                        </>
                      )}
                    </Button>
                  </label>
                </div>

                {promoImages.length === 0 ? (
                  <div
                    className="rounded-xl border border-dashed border-white/15 bg-black/20 p-10 text-center"
                    data-testid="car-promo-empty"
                  >
                    <ImagePlus className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
                    <p className="text-sm text-zinc-400">
                      Aún no hay imágenes. Sube la primera para activar el hero de la landing.
                    </p>
                  </div>
                ) : (
                  <div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                    data-testid="car-promo-grid"
                  >
                    {promoImages.map((url, idx) => (
                      <div
                        key={url + idx}
                        className="relative group rounded-lg overflow-hidden border border-white/10 bg-black/20"
                        data-testid={`car-promo-item-${idx}`}
                      >
                        <img
                          src={url}
                          alt={`Promoción ${idx + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handlePromoDelete(url)}
                          className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition opacity-0 group-hover:opacity-100"
                          data-testid={`car-promo-delete-${idx}`}
                          aria-label="Eliminar imagen"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="citas">
            <Card>
              <CardHeader><CardTitle data-testid="car-appointments-title">Citas de Mi Auto ({appointments.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2" data-testid="car-appointments-list">
                {appointments.map(item => <div key={item.id} className="border rounded p-3" data-testid={`car-appointment-item-${item.id}`}>{item.customer_name || 'Cliente'} • {item.model || 'Modelo'} • {item.service_date || 'Sin fecha'} • {item.status}</div>)}
                {!appointments.length && <p className="text-sm text-zinc-400" data-testid="car-appointments-empty">Sin citas todavía.</p>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={vehicleOpen} onOpenChange={setVehicleOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="car-vehicle-modal">
            <DialogHeader>
              <DialogTitle data-testid="car-vehicle-modal-title">{editingVehicleId ? 'Editar Modelo / Gama' : 'Nuevo Modelo'} <span className="text-xs text-zinc-500 ml-2">DMS Profesional</span></DialogTitle>
            </DialogHeader>

            <Tabs value={vehicleStep} onValueChange={setVehicleStep} data-testid="car-vehicle-modal-tabs">
              <TabsList className="grid grid-cols-6">
                <TabsTrigger value="general" data-testid="car-vehicle-tab-general">1. General</TabsTrigger>
                <TabsTrigger value="technical" data-testid="car-vehicle-tab-technical">2. Ficha Técnica</TabsTrigger>
                <TabsTrigger value="pricing" data-testid="car-vehicle-tab-pricing">3. Precios/Inventario</TabsTrigger>
                <TabsTrigger value="media" data-testid="car-vehicle-tab-media">4. Galería</TabsTrigger>
                <TabsTrigger value="equipment" data-testid="car-vehicle-tab-equipment">5. Equipamiento</TabsTrigger>
                <TabsTrigger value="campaigns" data-testid="car-vehicle-tab-campaigns">6. Campanas</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-3">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-zinc-400 flex items-center gap-1">Marca <Hint text="Selecciona una marca frecuente en México. Si no existe, usa 'Otra'." testid="hint-general-brand" /></label>
                    <Select value={vehicleForm.general.brand || ''} onValueChange={(v) => setVehicleForm(s => ({ ...s, general: { ...s.general, brand: v } }))}>
                      <SelectTrigger data-testid="vehicle-brand-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{MEXICO_BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {vehicleForm.general.brand === 'Otra' && (
                    <div className="space-y-1"><label className="text-xs text-zinc-400">Otra marca</label><Input value={vehicleForm.general.brand_other || ''} onChange={e => setVehicleForm(s => ({ ...s, general: { ...s.general, brand_other: e.target.value } }))} data-testid="vehicle-brand-other-input" /></div>
                  )}
                  <div className="space-y-1"><label className="text-xs text-zinc-400">Modelo</label><Input value={vehicleForm.general.model || ''} onChange={e => setVehicleForm(s => ({ ...s, general: { ...s.general, model: e.target.value } }))} data-testid="vehicle-model-input" /></div>
                  <div className="space-y-1"><label className="text-xs text-zinc-400">Versión / Gama / Trim (obligatorio)</label><Input value={vehicleForm.general.trim || ''} onChange={e => setVehicleForm(s => ({ ...s, general: { ...s.general, trim: e.target.value } }))} data-testid="vehicle-trim-input" /></div>
                  <div className="space-y-1"><label className="text-xs text-zinc-400">Año</label><Input type="number" value={vehicleForm.general.year || ''} onChange={e => setVehicleForm(s => ({ ...s, general: { ...s.general, year: e.target.value } }))} data-testid="vehicle-year-input" /></div>
                  <div className="space-y-1"><label className="text-xs text-zinc-400">Carrocería</label><Select value={vehicleForm.general.body_type || ''} onValueChange={v => setVehicleForm(s => ({ ...s, general: { ...s.general, body_type: v } }))}><SelectTrigger data-testid="vehicle-body-type-select"><SelectValue /></SelectTrigger><SelectContent>{BODY_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1"><label className="text-xs text-zinc-400">Categoría</label><Select value={vehicleForm.general.category || 'Nuevo'} onValueChange={v => setVehicleForm(s => ({ ...s, general: { ...s.general, category: v } }))}><SelectTrigger data-testid="vehicle-category-select"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                </div>
              </TabsContent>

              <TabsContent value="technical" className="space-y-3">
                <div className="grid md:grid-cols-4 gap-2">
                  {[
                    ['engine_displacement', 'Cilindrada'], ['engine_type', 'Tipo de motor'], ['cylinders', '# cilindros'], ['power_hp', 'Potencia (HP)'],
                    ['power_kw', 'Potencia (kW)'], ['torque_nm', 'Torque (Nm)'], ['transmission_type', 'Transmisión'], ['gears', 'Velocidades'],
                    ['traction', 'Tracción'], ['fuel_type', 'Combustible'], ['eff_city', 'Rend. ciudad'], ['eff_highway', 'Rend. carretera'],
                    ['eff_combined', 'Rend. combinado'], ['tank_capacity', 'Cap. tanque'], ['length', 'Largo'], ['width', 'Ancho'],
                    ['height', 'Alto'], ['wheelbase', 'Distancia entre ejes'], ['ground_clearance', 'Altura al suelo'], ['trunk_capacity', 'Cajuela (L)'], ['seats', 'Plazas']
                  ].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <label className="text-xs text-zinc-400">{label}</label>
                      <Input value={vehicleForm.technical?.[k] || ''} onChange={e => setVehicleForm(s => ({ ...s, technical: { ...s.technical, [k]: e.target.value } }))} data-testid={`vehicle-tech-${k}`} />
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-3">
                <div className="grid md:grid-cols-4 gap-2">
                  {[
                    ['list_price', 'Precio de lista'], ['sale_price', 'Precio de venta (promocional)'], ['max_discount', 'Descuento máximo'], ['dealer_special_price', 'Precio especial agencia'],
                    ['stock', 'Stock real'], ['vin', 'VIN (seminuevos)'], ['mileage', 'Kilometraje'], ['condition', 'Estado seminuevo'], ['location', 'Ubicación física']
                  ].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <label className="text-xs text-zinc-400">{label}</label>
                      <Input value={vehicleForm.pricing_inventory?.[k] || ''} onChange={e => setVehicleForm(s => ({ ...s, pricing_inventory: { ...s.pricing_inventory, [k]: e.target.value } }))} data-testid={`vehicle-pricing-${k}`} />
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="media" className="space-y-3">
                {(() => {
                  const gallery = vehicleForm.media?.gallery || [];
                  const handleMediaUpload = async (rawFiles) => {
                    const files = Array.from(rawFiles || []).filter(f => f && f.type && f.type.startsWith('image/'));
                    if (!files.length) return;
                    setMediaUploading(c => c + files.length);
                    const uploads = await Promise.all(files.map(async (file) => {
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        return { ok: true, url: res.data.url };
                      } catch (err) {
                        toast.error(`Error subiendo ${file.name}`);
                        return { ok: false };
                      }
                    }));
                    const successItems = uploads
                      .filter(u => u.ok && u.url)
                      .map(u => ({ url: u.url, is_360: false, label: '' }));
                    if (successItems.length) {
                      setVehicleForm(s => ({
                        ...s,
                        media: { ...s.media, gallery: [...(s.media?.gallery || []), ...successItems] },
                      }));
                      toast.success(`${successItems.length} imagen(es) subida(s)`);
                    }
                    setMediaUploading(c => Math.max(0, c - files.length));
                    if (mediaFileInputRef.current) mediaFileInputRef.current.value = '';
                  };
                  const removeImage = (idx) => {
                    setVehicleForm(s => ({
                      ...s,
                      media: { ...s.media, gallery: (s.media?.gallery || []).filter((_, i) => i !== idx) },
                    }));
                  };
                  const toggle360 = (idx) => {
                    setVehicleForm(s => ({
                      ...s,
                      media: {
                        ...s.media,
                        gallery: (s.media?.gallery || []).map((img, i) => i === idx ? { ...img, is_360: !img.is_360 } : img),
                      },
                    }));
                  };
                  return (
                    <>
                      <div
                        onDrop={(e) => { e.preventDefault(); handleMediaUpload(e.dataTransfer?.files); }}
                        onDragOver={(e) => e.preventDefault()}
                        onClick={() => mediaFileInputRef.current?.click()}
                        className="rounded-xl border-2 border-dashed border-white/15 bg-black/20 p-6 text-center cursor-pointer hover:border-amber-400/40 transition"
                        data-testid="vehicle-media-dropzone"
                      >
                        <input
                          ref={mediaFileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleMediaUpload(e.target.files)}
                          className="hidden"
                          data-testid="vehicle-media-file-input"
                        />
                        <div className="flex flex-col items-center gap-1">
                          <ImagePlus className="w-8 h-8 text-amber-400" />
                          <p className="text-sm text-white">Arrastra fotos aquí o haz clic para seleccionar</p>
                          <p className="text-[11px] text-zinc-400">Formatos imagen, múltiple. Mínimo recomendado: 8-12 fotos.</p>
                          <Button type="button" size="sm" variant="outline" className="mt-2 border-white/20" onClick={(e) => { e.stopPropagation(); mediaFileInputRef.current?.click(); }} data-testid="vehicle-media-upload-btn">
                            <Upload className="w-3.5 h-3.5 mr-1" /> Subir fotos
                          </Button>
                        </div>
                      </div>

                      {mediaUploading > 0 && (
                        <p className="text-xs text-amber-300 flex items-center gap-2" data-testid="vehicle-media-uploading">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo {mediaUploading} imagen(es)...
                        </p>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="vehicle-gallery-preview-grid">
                        {gallery.map((img, idx) => (
                          <div key={`${img.url}-${idx}`} className="relative rounded-lg border border-white/10 bg-black/30 overflow-hidden group" data-testid={`vehicle-gallery-item-${idx}`}>
                            <img src={img.url} alt={`Vehículo ${idx + 1}`} className="w-full h-32 object-cover" />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500/80 text-white flex items-center justify-center transition opacity-90"
                              title="Eliminar de la galería"
                              data-testid={`vehicle-gallery-remove-${idx}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggle360(idx)}
                              className={`absolute bottom-1.5 left-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border transition ${img.is_360 ? 'bg-amber-500 text-black border-amber-400' : 'bg-black/70 text-zinc-200 border-white/20 hover:bg-black/90'}`}
                              title="Marcar como imagen 360°"
                              data-testid={`vehicle-gallery-toggle-360-${idx}`}
                            >
                              360°
                            </button>
                          </div>
                        ))}
                        {!gallery.length && mediaUploading === 0 && (
                          <p className="col-span-full text-xs text-zinc-500 text-center py-4" data-testid="vehicle-gallery-empty">
                            No hay fotos cargadas todavía.
                          </p>
                        )}
                      </div>

                      <div className="grid md:grid-cols-2 gap-2 pt-2">
                        <Input value={vehicleForm.media?.video_url || ''} onChange={e => setVehicleForm(s => ({ ...s, media: { ...s.media, video_url: e.target.value } }))} placeholder="Video promocional (YouTube o URL)" data-testid="vehicle-video-url-input" />
                        <Input value={vehicleForm.media?.tech_sheet_pdf_url || ''} onChange={e => setVehicleForm(s => ({ ...s, media: { ...s.media, tech_sheet_pdf_url: e.target.value } }))} placeholder="Ficha técnica PDF (URL)" data-testid="vehicle-techsheet-input" />
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              <TabsContent value="equipment" className="space-y-3">
                <div className="grid md:grid-cols-3 gap-2 max-h-[340px] overflow-auto p-1 border border-white/10 rounded" data-testid="vehicle-equipment-checklist">
                  {EQUIPMENT_ITEMS.map(item => (
                    <label key={item} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={(vehicleForm.equipment?.checklist || []).includes(item)} onChange={(e) => setVehicleForm(s => {
                        const current = new Set(s.equipment?.checklist || []);
                        if (e.target.checked) current.add(item); else current.delete(item);
                        return { ...s, equipment: { ...s.equipment, checklist: Array.from(current) } };
                      })} data-testid={`vehicle-equipment-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                      {item}
                    </label>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <div><label className="text-xs text-zinc-400">Accesorios incluidos (uno por línea)</label><Textarea value={formatLines(vehicleForm.equipment?.included_accessories || [])} onChange={e => setVehicleForm(s => ({ ...s, equipment: { ...s.equipment, included_accessories: parseLines(e.target.value) } }))} data-testid="vehicle-included-accessories" /></div>
                  <div><label className="text-xs text-zinc-400">Accesorios opcionales (uno por línea)</label><Textarea value={formatLines(vehicleForm.equipment?.optional_accessories || [])} onChange={e => setVehicleForm(s => ({ ...s, equipment: { ...s.equipment, optional_accessories: parseLines(e.target.value) } }))} data-testid="vehicle-optional-accessories" /></div>
                </div>
              </TabsContent>

              <TabsContent value="campaigns" className="space-y-3">
                {editingVehicleId
                  ? <div className="rounded-lg border border-white/5 bg-black/20 p-3 text-xs text-muted-foreground">Las campañas se configuran en <a href="/settings?tab=campanas" className="text-violet-400 underline">Configuración → Campañas</a>, en un solo lugar. Ahí eliges el alcance (modelo) de cada campaña.</div>
                  : <p className="text-xs text-zinc-400 italic">Guarda el modelo primero para crear sus campanas.</p>}
              </TabsContent>

            </Tabs>

            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-white/10" data-testid="vehicle-modal-actions">
              <Button variant="outline" onClick={() => setVehicleOpen(false)} data-testid="vehicle-cancel-button">Cancelar</Button>
              <Button variant="secondary" onClick={() => saveVehicle(true)} data-testid="vehicle-save-and-new-trim-button"><RefreshCw className="w-3.5 h-3.5 mr-1" />Guardar y crear otra versión/gama</Button>
              <Button onClick={() => saveVehicle(false)} data-testid="vehicle-save-button">Guardar modelo</Button>
            </div>
          </DialogContent>
        </Dialog>

        {loading && <p className="text-xs text-zinc-500" data-testid="car-loading-indicator">Cargando...</p>}

        {usedModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="car-used-modal">
            <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-bold text-white">
                  {editingUsed ? 'Editar Seminuevo' : 'Nuevo Seminuevo'}
                </h2>
                <button onClick={() => setUsedModalOpen(false)} className="text-zinc-400 hover:text-white text-2xl" data-testid="car-used-modal-close">✕</button>
              </div>

              <div className="flex border-b border-white/10 px-6">
                {[
                  { id: 'datos',   label: 'Vehículo' },
                  { id: 'precios', label: 'Precio e Inventario' },
                  { id: 'galeria', label: 'Galería' },
                  { id: 'config',  label: 'Configuración' },
                ].map(t => (
                  <button key={t.id} onClick={() => setUsedTab(t.id)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      usedTab === t.id
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid={`car-used-tab-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {usedTab === 'datos' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Marca</label>
                      <select value={usedForm.marca}
                        onChange={e => setUsedForm(p => ({ ...p, marca: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        data-testid="car-used-marca">
                        <option value="">Selecciona marca</option>
                        {MEXICO_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Modelo</label>
                      <input type="text" value={usedForm.modelo}
                        onChange={e => setUsedForm(p => ({ ...p, modelo: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="Ej. Aveo, Tucson..."
                        data-testid="car-used-modelo" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Año</label>
                      <input type="number" value={usedForm.año}
                        onChange={e => setUsedForm(p => ({ ...p, año: parseInt(e.target.value) || 0 }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        min="1990" max="2030"
                        data-testid="car-used-año" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Carrocería</label>
                      <select value={usedForm.carroceria}
                        onChange={e => setUsedForm(p => ({ ...p, carroceria: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm">
                        {['Hatchback','SUV','Pick-Up','Sedán','Van','Coupé','Convertible'].map(c =>
                          <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Transmisión</label>
                      <select value={usedForm.transmision}
                        onChange={e => setUsedForm(p => ({ ...p, transmision: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm">
                        <option value="Automática">Automática</option>
                        <option value="Manual">Manual</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Combustible</label>
                      <select value={usedForm.combustible}
                        onChange={e => setUsedForm(p => ({ ...p, combustible: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm">
                        {['Gasolina','Diesel','Eléctrico','Híbrido'].map(c =>
                          <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Puertas</label>
                      <select value={usedForm.puertas}
                        onChange={e => setUsedForm(p => ({ ...p, puertas: parseInt(e.target.value) }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm">
                        {[2,3,4,5].map(n => <option key={n} value={n}>{n} puertas</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Color exterior</label>
                      <input type="text" value={usedForm.color_exterior}
                        onChange={e => setUsedForm(p => ({ ...p, color_exterior: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="Ej. Blanco, Negro..." />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Color interior</label>
                      <input type="text" value={usedForm.color_interior}
                        onChange={e => setUsedForm(p => ({ ...p, color_interior: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="Ej. Negro, Beige..." />
                    </div>
                  </div>
                )}

                {usedTab === 'precios' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Precio ($)</label>
                      <input type="number" value={usedForm.precio}
                        onChange={e => setUsedForm(p => ({ ...p, precio: parseFloat(e.target.value) || 0 }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm" min="0"
                        data-testid="car-used-precio" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Kilometraje</label>
                      <input type="number" value={usedForm.kilometraje}
                        onChange={e => setUsedForm(p => ({ ...p, kilometraje: parseInt(e.target.value) || 0 }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Estatus</label>
                      <select value={usedForm.estatus}
                        onChange={e => setUsedForm(p => ({ ...p, estatus: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm">
                        <option value="Disponible">Disponible</option>
                        <option value="Apartado">Apartado</option>
                        <option value="Vendido">Vendido</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">VIN</label>
                      <input type="text" value={usedForm.vin}
                        onChange={e => setUsedForm(p => ({ ...p, vin: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="Opcional" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-zinc-300 mb-1">Ubicación</label>
                      <input type="text" value={usedForm.location}
                        onChange={e => setUsedForm(p => ({ ...p, location: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="Ej. Sucursal Norte" />
                    </div>
                  </div>
                )}

                {usedTab === 'galeria' && (
                  <div className="space-y-4">
                    <div
                      className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-500/5 transition-colors"
                      onClick={() => document.getElementById('used-photo-input').click()}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); handleUsedPhotoUpload(e.dataTransfer.files); }}>
                      <input id="used-photo-input" type="file" accept="image/*" multiple className="hidden"
                        onChange={e => handleUsedPhotoUpload(e.target.files)} />
                      {uploadingUsedPhoto ? (
                        <p className="text-blue-400 text-sm">Subiendo fotos...</p>
                      ) : (
                        <>
                          <svg className="w-10 h-10 mx-auto text-zinc-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm text-zinc-300">Arrastra fotos aquí o <span className="text-blue-400 font-medium">haz clic para seleccionar</span></p>
                          <p className="text-xs text-zinc-500 mt-1">La primera foto es la imagen principal · Máx. 15MB por foto</p>
                        </>
                      )}
                    </div>

                    {usedForm.gallery.length > 0 && (
                      <div className="grid grid-cols-3 gap-3">
                        {usedForm.gallery.map((img, idx) => (
                          <div key={idx} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-video bg-zinc-800">
                            <img src={img.url} alt={`foto-${idx}`} className="w-full h-full object-cover" />
                            {idx === 0 && (
                              <span className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded">Principal</span>
                            )}
                            {img.is_360 && (
                              <span className="absolute top-1 right-6 bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded">360°</span>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <button onClick={() => toggleUsed360(idx)} title="Toggle 360°"
                                className="bg-white/90 text-purple-700 text-xs px-2 py-1 rounded hover:bg-white">
                                360°
                              </button>
                              <button onClick={() => removeUsedPhoto(idx)}
                                className="bg-white/90 text-red-600 text-xs px-2 py-1 rounded hover:bg-white">
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">URL de video (opcional)</label>
                      <input type="url" value={usedForm.video_url}
                        onChange={e => setUsedForm(p => ({ ...p, video_url: e.target.value }))}
                        className="w-full border border-white/10 bg-black/30 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="https://youtube.com/..." />
                    </div>
                  </div>
                )}

                {usedTab === 'config' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-white/10 rounded-xl">
                      <div>
                        <p className="font-medium text-white">Vehículo activo</p>
                        <p className="text-sm text-zinc-400">Los vehículos inactivos no aparecen en el inventario público</p>
                      </div>
                      <button
                        onClick={() => setUsedForm(p => ({ ...p, is_active: !p.is_active }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          usedForm.is_active ? 'bg-blue-600' : 'bg-zinc-600'
                        }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          usedForm.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-white/10">
                <button onClick={() => setUsedModalOpen(false)}
                  className="px-4 py-2 border border-white/10 rounded-lg text-zinc-200 hover:bg-white/5 text-sm">
                  Cancelar
                </button>
                <button onClick={saveUsedVehicle}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  data-testid="car-used-save-btn">
                  {editingUsed ? 'Guardar cambios' : 'Crear seminuevo'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
